import React from 'react';
import { useIntl } from 'react-intl';
import clsx from 'clsx';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCalendar } from '@fortawesome/pro-light-svg-icons/faCalendar';
import { TypedMemo } from '../../../common/types';
import { useShowToast } from '../../../../redux/slices/toasts/hooks';
import { useReplyThread } from '../hooks/useReplyThread';
import type { InboxAttachment, ReplyThread } from '../outreachTypes';
import { shortDate } from '../outreachFormat';
import { inboxMessages as messages } from './inboxMessages';
/* eslint-disable css-modules/no-unused-class -- one stylesheet for Outreach; each part uses its own slice of it */
import shared from '../Outreach.module.scss';
/* eslint-enable css-modules/no-unused-class */

interface LiveLinkReminderChipProps {
  accountId: number;
  thread: ReplyThread;
  attachments: InboxAttachment[];
  disabled: boolean;
}

/** True when the song is not out yet: the pre-save chip is the only link then (`attachmentsOf`). */
function songIsComing(attachments: InboxAttachment[]): boolean {
  return attachments.some((attachment) => attachment.kind === 'presaveLink' && attachment.available);
}

/**
 * "Send the live link again on Oct 28" (design outreach-v2 m1, `inbox.remindLiveLink`): on the
 * release day Plug writes a second answer with the live link and holds it for the person. Offered
 * when they ask for the link and the song is not out; shown while the task is open.
 */
const LiveLinkReminderChipComponent: React.FC<LiveLinkReminderChipProps> = ({
  accountId,
  thread,
  attachments,
  disabled,
}) => {
  const intl = useIntl();
  const { showError } = useShowToast();
  const { remindLiveLink, isReminding } = useReplyThread(accountId, thread.replyUuid);
  const reminder = thread.liveLinkReminder;
  if (reminder !== null) {
    return (
      <span className={clsx(shared.chip, shared.chipVio)} data-testid="live-link-reminder">
        <FontAwesomeIcon icon={faCalendar} />
        {intl.formatMessage(messages.reminderSet, { date: shortDate(intl, `${reminder.dueDay}T12:00:00Z`) })}
      </span>
    );
  }
  if (!thread.asks.includes('link') || !songIsComing(attachments)) {
    return null;
  }
  return (
    <button
      type="button"
      className={clsx(shared.chip, shared.chipButton)}
      disabled={disabled || isReminding}
      onClick={() =>
        remindLiveLink(thread.replyUuid).catch((error: unknown) => {
          console.error(error);
          showError(intl.formatMessage(messages.reminderFailed));
        })
      }
      data-testid="live-link-reminder-ask"
    >
      <FontAwesomeIcon icon={faCalendar} />
      {intl.formatMessage(messages.reminderAsk)}
    </button>
  );
};

export const LiveLinkReminderChip = TypedMemo(LiveLinkReminderChipComponent);
